import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ErrorBanner, formatUtc, useAsync } from '../../components/Async'
import { PageTitle } from '../../components/PageTitle'
import {
  ApiError,
  api,
  assistant,
  type AssistantDictionaryDraft,
  type AssistantDraft,
  type PolicySummary,
  type TermReview
} from '../../lib/api'

/**
 * The assistant: drafting, explaining, and the settings that decide what it may spend.
 *
 * Two things are said plainly on this screen rather than buried in documentation, because both
 * change what an operator should do. Prompts leave the building — redacted, and the audit shows
 * exactly what was sent — so anybody nervous about that can look rather than wonder. And every
 * draft is a draft: it goes into the ordinary editor, where a person reviews it and saves it, and
 * nothing here can publish anything.
 *
 * When the provider is unavailable the page still works. The settings render, the term review still
 * gives its verdict — that part is arithmetic, not a model — and drafting says so in a sentence
 * instead of an error.
 */
export function AssistantPage() {
  const navigate = useNavigate()
  const settings = useAsync(() => assistant.settings(), [])
  const models = useAsync(() => assistant.models(), [])
  const policies = useAsync(() => api.listPolicies(), [])

  const [enabled, setEnabled] = useState(false)
  const [model, setModel] = useState('')
  const [tokenBudget, setTokenBudget] = useState<string>('')
  const [spendCap, setSpendCap] = useState<string>('')

  const [intent, setIntent] = useState('')
  const [draft, setDraft] = useState<AssistantDraft | null>(null)
  const [dictionary, setDictionary] = useState<AssistantDictionaryDraft | null>(null)
  const [terms, setTerms] = useState('confidential\ncustomer account list')
  const [review, setReview] = useState<TermReview | null>(null)
  const [explaining, setExplaining] = useState('')
  const [explanation, setExplanation] = useState<string | null>(null)

  const [failure, setFailure] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!settings.value) return
    setEnabled(settings.value.enabled)
    setModel(settings.value.model)
    setTokenBudget(settings.value.monthlyTokenBudget?.toString() ?? '')
    setSpendCap(settings.value.monthlySpendCapUsd?.toString() ?? '')
  }, [settings.value])

  const run = useCallback(async (work: () => Promise<string | null>) => {
    setBusy(true)
    setFailure(null)
    setNotice(null)
    try {
      const message = await work()
      if (message) setNotice(message)
    } catch (error) {
      // A 503 is the provider being down rather than a defect here, and it reads as one.
      setFailure(error instanceof ApiError ? error.message : 'The request failed.')
    } finally {
      setBusy(false)
    }
  }, [])

  const save = () =>
    run(async () => {
      await assistant.saveSettings({
        enabled,
        model,
        monthlyTokenBudget: tokenBudget === '' ? null : Number(tokenBudget),
        monthlySpendCapUsd: spendCap === '' ? null : Number(spendCap)
      })
      settings.reload()
      return 'Saved.'
    })

  const drafting = () =>
    run(async () => {
      const result = await assistant.draftPolicy(intent)
      setDraft(result)
      settings.reload()
      return result.accepted
        ? 'The draft passes validation. Open it in the editor to review and save it.'
        : null
    })

  const draftingDictionary = () =>
    run(async () => {
      const result = await assistant.draftDictionary(intent)
      setDictionary(result)
      settings.reload()
      return result.succeeded ? `${result.rules.length} rule(s) drafted.` : null
    })

  const reviewing = () =>
    run(async () => {
      const result = await assistant.reviewTerms(
        terms.split('\n').map((term) => term.trim()).filter((term) => term.length > 0)
      )
      setReview(result)
      settings.reload()
      return null
    })

  const explain = (policyId: string) =>
    run(async () => {
      setExplaining(policyId)
      const result = await assistant.explainPolicy(policyId)
      setExplanation(result.succeeded ? result.explanation : result.problem)
      settings.reload()
      return null
    })

  const value = settings.value
  const available = value?.available ?? false

  return (
    <>
      <div className="page-header">
        <div>
          <PageTitle>Assistant</PageTitle>
          <p>Drafts policies and dictionaries, and explains the ones you already have.</p>
        </div>
      </div>

      <ErrorBanner message={failure ?? settings.error} />
      {notice && (
        <div className="banner success" role="status" data-testid="assistant-notice">
          {notice}
        </div>
      )}

      {value && !available && (
        <div className="banner" data-testid="assistant-unavailable">
          {value.unavailable ?? 'The assistant is unavailable.'} Policies and dictionaries are
          authored by hand in the meantime, which is the same editor either way.
        </div>
      )}

      {/* One banner stating both facts, because they were two and they read as contradictory: the page
          said answers were canned, and then acting on a control answered that the assistant was not
          enabled at all. Whether it is switched on decides what happens when somebody clicks; which
          provider is behind it decides what comes back. An operator needs both before clicking. */}
      {value?.provider === 'stub' && (
        <div className="banner" data-testid="assistant-stub">
          This deployment is running the development stub, so answers are canned and no provider is
          called.{' '}
          {enabled
            ? 'The assistant is switched on for this organization, so the controls below will answer from the script.'
            : 'It is also switched off for this organization, so the controls below will decline until you enable it above.'}
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <h3 style={{ marginTop: 0 }}>Settings</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            Prompts are redacted before they are sent — email addresses, device names, identifiers,
            and your organization's name are replaced — and every request is recorded below with the
            exact text that left the building.
          </p>

          <div className="toolbar">
            <label className="field">
              Enabled
              <input
                type="checkbox"
                checked={enabled}
                onChange={(event) => setEnabled(event.target.checked)}
                disabled={!available}
                data-testid="assistant-enabled"
              />
            </label>
            <label className="field">
              Model
              <select
                value={model}
                onChange={(event) => setModel(event.target.value)}
                data-testid="assistant-model"
              >
                {(models.value?.models ?? []).map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.name}
                  </option>
                ))}
                {models.value && !models.value.models.some((candidate) => candidate.id === model) && (
                  <option value={model}>{model}</option>
                )}
              </select>
            </label>
            <label className="field">
              Tokens a month
              <input
                type="number"
                value={tokenBudget}
                placeholder="no ceiling"
                onChange={(event) => setTokenBudget(event.target.value)}
                data-testid="assistant-token-budget"
              />
            </label>
            <label className="field">
              Spend a month (USD)
              <input
                type="number"
                value={spendCap}
                placeholder="no ceiling"
                onChange={(event) => setSpendCap(event.target.value)}
                data-testid="assistant-spend-cap"
              />
            </label>
            <button type="button" className="primary" onClick={save} disabled={busy} data-testid="save-assistant">
              Save
            </button>
          </div>

          {value && (
            <p className="muted" data-testid="assistant-usage">
              {value.tokensUsedThisMonth.toLocaleString()} tokens and ${value.spentThisMonthUsd.toFixed(4)} used
              this month across {value.requestsThisMonth} request(s).
              {models.value && !models.value.fromProvider && ' The model list is the built-in one; the provider did not answer.'}
            </p>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <h3 style={{ marginTop: 0 }}>Draft from a description</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            Say what should be watched for and what should happen. The draft opens in the ordinary
            editor, where it is reviewed and saved like any other.
          </p>

          <textarea
            rows={3}
            value={intent}
            onChange={(event) => setIntent(event.target.value)}
            placeholder="Watch ChatGPT and Claude for customer records being pasted in, every five minutes."
            data-testid="assistant-intent"
            style={{ width: '100%' }}
          />

          <div className="toolbar">
            <button type="button" className="primary" onClick={drafting} disabled={busy || intent.length === 0} data-testid="draft-policy">
              Draft a policy
            </button>
            <button type="button" onClick={draftingDictionary} disabled={busy || intent.length === 0} data-testid="draft-dictionary">
              Draft dictionary rules
            </button>
          </div>

          {draft && (
            <div data-testid="policy-draft">
              {draft.problem && <div className="banner">{draft.problem}</div>}
              {draft.documentJson && (
                <>
                  <pre className="mono" style={{ whiteSpace: 'pre-wrap', overflowX: 'auto' }} data-testid="draft-document">
                    {draft.documentJson}
                  </pre>
                  <div className="toolbar">
                    <button
                      type="button"
                      className="primary"
                      data-testid="open-draft"
                      onClick={() => navigate('/policies/new', { state: { draft: draft.documentJson } })}
                    >
                      Open in the editor
                    </button>
                    <span className="muted">
                      {draft.redactions} redaction(s) · ${draft.costUsd.toFixed(4)}
                    </span>
                  </div>
                </>
              )}
              {draft.findings.length > 0 && (
                <ul data-testid="draft-findings">
                  {draft.findings.map((finding, index) => (
                    <li key={`${finding.fieldPath}-${index}`}>
                      <span className="pill">{finding.severity}</span> {finding.fieldPath}: {finding.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {dictionary && (
            <div data-testid="dictionary-draft">
              {dictionary.problem && <div className="banner">{dictionary.problem}</div>}
              <ul>
                {dictionary.rules.map((rule) => (
                  <li key={rule.name}>
                    <strong>{rule.name}</strong>
                    {rule.severity && <span className="pill">{rule.severity}</span>}
                    {rule.isSignal && <span className="pill">signal</span>}
                    <div className="muted">{rule.terms.join(' · ')}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <h3 style={{ marginTop: 0 }}>Review terms</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            One term per line. The verdict is the rule the client's own presets follow — two- and
            three-word phrases — and it holds whether or not the assistant is available.
          </p>

          <textarea
            rows={3}
            value={terms}
            onChange={(event) => setTerms(event.target.value)}
            data-testid="assistant-terms"
            style={{ width: '100%' }}
          />

          <div className="toolbar">
            <button type="button" onClick={reviewing} disabled={busy} data-testid="review-terms">
              Review
            </button>
          </div>

          {review && (
            <div className="table-scroll">
              <table data-testid="term-review">
                <thead>
                  <tr>
                    <th>Term</th>
                    <th>Verdict</th>
                    <th className="secondary">Why</th>
                  </tr>
                </thead>
                <tbody>
                  {review.terms.map((term) => (
                    <tr key={term.term}>
                      <td className="mono">{term.term}</td>
                      <td>
                        <span className="pill">{term.specific ? 'specific' : 'too broad'}</span>
                      </td>
                      <td className="muted secondary">
                        {term.reason}
                        {term.suggestion && ` Try "${term.suggestion}".`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {review.advice && <pre className="mono" style={{ whiteSpace: 'pre-wrap' }}>{review.advice}</pre>}
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <h3 style={{ marginTop: 0 }}>Explain a policy</h3>
          <div className="toolbar">
            <select
              value={explaining}
              onChange={(event) => explain(event.target.value)}
              disabled={busy}
              data-testid="explain-policy"
            >
              <option value="">Choose a policy…</option>
              {(policies.value?.items ?? []).map((policy: PolicySummary) => (
                <option key={policy.policyId} value={policy.policyId}>
                  {policy.name}
                </option>
              ))}
            </select>
          </div>
          {explanation && (
            <p data-testid="policy-explanation">{explanation}</p>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <h3 style={{ marginTop: 0 }}>What was sent</h3>
          <AssistantAudit />
        </div>
      </div>
    </>
  )
}

/**
 * The audit, as the operator's own record of what left the building.
 *
 * On the same page as the feature rather than hidden in an administration section: the reason it
 * exists is that somebody can check, and a check nobody can find is not one.
 */
function AssistantAudit() {
  const audit = useAsync(() => assistant.audit(), [])

  if ((audit.value?.items.length ?? 0) === 0)
    return <div className="banner" data-testid="assistant-audit-empty">Nothing has been sent yet.</div>

  return (
    <div className="table-scroll">
      <table data-testid="assistant-audit">
        <thead>
          <tr>
            <th className="secondary">When</th>
            <th>Purpose</th>
            <th>Outcome</th>
            <th>Sent</th>
            <th className="secondary">Cost</th>
          </tr>
        </thead>
        <tbody>
          {audit.value?.items.map((entry) => (
            <tr key={entry.requestId}>
              <td className="muted secondary">{formatUtc(entry.createdUtc)}</td>
              <td>{entry.purpose}</td>
              <td>
                <span className="pill">{entry.outcome}</span>
                {entry.problem && <div className="muted">{entry.problem}</div>}
              </td>
              <td className="mono">
                {entry.redactedPrompt}
                <div className="muted">{entry.redactions} redaction(s)</div>
              </td>
              <td className="muted secondary">
                ${entry.costUsd.toFixed(4)}
                <div>{entry.promptTokens + entry.completionTokens} tokens</div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
